/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  Grid,
  Box,
  IconButton,
  Alert,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  // RadioGroup,
  // FormControlLabel,
  // Radio,
  Chip,
  Tooltip,
  Stack,
  Portal,
  Snackbar,
} from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TextField from '@mui/material/TextField';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { Trash, X, Save, Copy } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { formatDateLabel } from 'modules/dashboard/functions';
import { selectHotelDropdownList } from 'modules/dashboard/selectors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { CustomToggleSwitch, Modal } from 'modules/common/components';
import { FormStyles, Typography } from '../style';
import { selectAlertById, selectAlertParam } from '../selectors';
import { formatAlertName, formatLabel } from '../functions';
import { alertActions } from '../slice';
import { updateAlertValidation } from '../validations/alert-validation';
import AlertDateRangePicker from './date-picker';
import { recurrenceOptions } from '../constants';

/**
 * Component to handle alert table right side drawer pane
 * @param {*} show - open/hide state of side drawer
 * @param {*} isShow - function to set show/hide of drawer
 * @param {*} alertId - alert id
 * @returns
 */
const AlertDrawerView = ({ show, isShow, alertId, isEditable }) => {
  const dispatch = useDispatch();

  const formElement = useRef(null);
  //
  const organizationId = useSelector(selectOrganizationId);
  const alert = useSelector((state) => selectAlertById(state, alertId));
  const alertParams = useSelector(selectAlertParam);
  const hotelList = useSelector(selectHotelDropdownList);
  const notification = useSelector(selectNotification);
  //
  const [isDelete, setDelete] = useState(false);
  const [isUpdate, setUpdate] = useState(false);

  const [alertOptions, setAlertOptions] = useState([]);
  const [alertConfigs, setAlertConfigs] = useState({});

  const [inputValue, setInputValue] = useState('');
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(() => {
    setCopied(true);
  }, []);
  //
  const handleClick = () => {
    setCopied(true);
  };

  const [dates, setDates] = useState({
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    key: 'selection',
  });

  const [initialValues, setInitialValues] = useState({
    id: '',
    alertName: '',
    alertCustomName: '',
    hotelCodes: '',
    targetDateType: 'stay_date',
    targetDateRecurrence: '',
    evalLead: [],
    evalDate: '',
    alertParams: {}, // For dynamic alert config fields
  });

  //
  // close modal after successful notification
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      isShow(false);
    }
  }, [notification]);

  //
  useEffect(() => {
    if (!Array.isArray(alertParams)) return;

    const options = alertParams.map((a) => ({
      label: formatAlertName(a.alertName),
      value: a.alertName,
    }));

    const configs = {};
    alertParams.forEach((a) => {
      // Create a shallow copy of alertParams object before adding new property
      configs[a.alertName] = {
        ...a.alertParams,
      };
    });

    setAlertOptions(options);
    setAlertConfigs(configs);
  }, [alertParams]);

  useEffect(() => {
    if (alert && alertOptions) {
      setInitialValues({
        id: alert?.id,
        alertName: alert.alertProc,
        alertCustomName: alert?.name,
        hotelCodes: alert?.hotelIds,
        targetDateRecurrence: alert?.targetDateRecurrence,
        evalLead: alert?.evalLead ?? [],
        evalDate: alert?.evalDate,
        alertParams: alert.alertParams, // For dynamic alert config fields
      });

      setDates((prev) => ({
        ...prev,
        startDate: alert.targetDateStart,
        endDate: alert?.targetDateEnd,
      }));
    }
  }, [alert, alertOptions, alertConfigs]);

  // close modal after successful notification
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      isShow(false);
    }
  }, [notification]);
  //
  const handleDateChange = (item) => {
    setDates((prev) => ({
      ...prev,
      startDate: item?.selection.startDate,
      endDate: item?.selection.endDate,
    }));
  };

  const onSubmitUpdateAlert = async (values) => {
    const valueObj = {
      name: values.alertCustomName,
      hotelIds: values.hotelCodes,
      alertProc: values.alertName,
      alertParams: {
        ...values.alertParams,
        ...(values.alertName !== 'pace_audit_vs_stlp' && { dayOfWeekAlignment: values.isAligned }),
      },
      targetDateType: 'stay_date',
      targetDateStart: new Date(dates.startDate).toISOString(),
      targetDateEnd: new Date(dates.endDate).toISOString(),
      targetDateRecurrence: values.targetDateRecurrence,
      evalLead: values.evalLead,
    };

    dispatch(
      alertActions.updateAlert({
        ...valueObj,
        organizationId,
        alertId: alert.id,
      })
    );
  };

  // delete user
  const deleteUser = (payload) => {
    dispatch(alertActions.deleteAlert(payload));
    setDelete(false);
  };
  //
  return (
    <Box
      xs={12}
      md={4}
      ml={4}
      mt={10}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{
        backgroundColor: 'white',
        flexShrink: 0,
        display: show ? 'flex' : 'none',
      }}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={updateAlertValidation}
        onSubmit={onSubmitUpdateAlert}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          handleSubmit,
          isSubmitting,
          touched,
          errors,
          handleBlur,
          dirty,
          handleReset,
        }) => {
          // Current alert config props or empty object
          const currentConfigProps = alertConfigs[values.alertName]?.properties || {};
          return (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container direction="row" justifyContent="space-between" px={2} mb={6}>
                <Typography
                  noWrap
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {alert?.name}
                </Typography>
                <Grid>
                  <Portal>
                    <Snackbar
                      autoHideDuration={4000}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      open={copied}
                      onClose={() => {
                        setCopied(false);
                      }}
                    >
                      <Alert severity="success" sx={{ width: '100%' }}>
                        Copy to clipboard!
                      </Alert>
                    </Snackbar>
                  </Portal>
                  <CopyToClipboard
                    disabled={!isEditable}
                    onCopy={onCopy}
                    text={JSON.stringify(values, null, 2)}
                  >
                    <IconButton
                      disabled={!isEditable}
                      onClick={handleClick}
                      color="inherit"
                      size="small"
                    >
                      <span>
                        <Tooltip title="Copy">
                          <Copy />
                        </Tooltip>
                      </span>
                    </IconButton>
                  </CopyToClipboard>
                  <Tooltip title="Update">
                    <span>
                      <IconButton
                        disabled={isSubmitting || !isEditable}
                        onClick={() => (errors.alertCustomName ? '' : setUpdate(true))}
                        color="inherit"
                        size="small"
                      >
                        <Save />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        disabled={!isEditable}
                        color="error"
                        size="small"
                        onClick={() => setDelete(true)}
                      >
                        <Trash />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (dirty) {
                        handleReset();
                      }
                      isShow(false);
                    }}
                  >
                    <X />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid container spacing={2} direction="column">
                {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                  <Alert mt={2} mb={3} severity={notification?.type}>
                    {notification?.message}
                  </Alert>
                )}
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <Autocomplete
                    disabled
                    options={alertOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                    value={alertOptions.find((opt) => opt.value === values.alertName) || null}
                    onChange={(e, selected) => {
                      setFieldValue('alertName', selected?.value || '');
                      // reset dynamic fields when alert changes
                      setFieldValue('alertParams', {});
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Type"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="alertCustomName"
                    value={values.alertCustomName}
                    error={Boolean(touched.alertCustomName && errors.alertCustomName)}
                    helperText={touched.alertCustomName && errors.alertCustomName}
                    disabled={!isEditable}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    label="Name"
                  />
                </FormControl>
              </Grid>

              {/* Hotel Selector */}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <Autocomplete
                    size="small"
                    multiple
                    disabled={!isEditable}
                    name="hotelCodes"
                    onBlur={handleBlur}
                    options={(hotelList ?? []).map((h) => ({
                      label: h.hotel || h.name,
                      value: h.hotelCode,
                    }))}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                    value={(hotelList ?? [])
                      .map((h) => ({ label: h.hotel || h.name, value: h.hotelCode }))
                      .filter((opt) => values.hotelCodes?.includes(opt.value))}
                    onChange={(e, selectedOptions) => {
                      // selectedOptions is an array of selected option objects
                      const selectedIds = selectedOptions?.map((opt) => opt.value) || [];
                      setFieldValue('hotelCodes', selectedIds);
                    }}
                    limitTags={2}
                    componentsProps={{
                      paper: {
                        sx: {
                          width: '90%',
                          marginLeft: '10%',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: 0,
                        height: 'fit-content',
                      },
                      marginY: 2,
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hotels"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(touched.hotelCodes && errors.hotelCodes)}
                        helperText={touched.hotelCodes && errors.hotelCodes}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 'auto',
                            overflow: 'auto',
                            maxHeight: 100,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Target Date Range */}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <AlertDateRangePicker
                    label="Target Date"
                    handleChange={handleDateChange}
                    ranges={[dates]}
                    dateValue={formatDateLabel(dates)}
                    cssClassName={styles.calendarElement}
                    disabled={!isEditable}
                  />
                </FormControl>
              </Grid>

              {/* Target Date Recurrence */}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <Autocomplete
                    disabled={!isEditable}
                    options={recurrenceOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                    value={
                      recurrenceOptions.find((opt) => opt.value === values.targetDateRecurrence) ||
                      null
                    }
                    onChange={(e, selected) => {
                      setFieldValue('targetDateRecurrence', selected?.value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Date Recurrence"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Target Date Recurrence Alignment */}
              {values.alertName !== 'pace_audit_vs_stlp' && (
                <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                  <FormControl sx={FormStyles} fullWidth>
                    <CustomToggleSwitch
                      disabled={!isEditable}
                      label="Day Of Week Alignment"
                      title=""
                      checked={values.alertParams.dayOfWeekAlignment}
                      onChange={(event) => {
                        setFieldValue('alertParams.dayOfWeekAlignment', event.target.checked);
                      }}
                    />
                  </FormControl>
                </Grid>
              )}

              {/* Evaluation Lead Time */}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <TextField
                        type="number"
                        label="Evaluation Lead Time"
                        placeholder="Enter lead time in days"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault(); // prevent Formik submit
                            setFieldValue('evalLead', [...values.evalLead, inputValue]);
                            setInputValue('');
                          }
                        }}
                        InputLabelProps={{
                          shrink: true,
                          sx: {
                            transform: 'translate(14px, -9px) scale(0.75)',
                            backgroundColor: 'white',
                            padding: '0 4px',
                          },
                        }}
                        disabled={values.evalLead?.length === 4 || !isEditable}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setFieldValue('evalLead', [...values.evalLead, inputValue]);
                          setInputValue('');
                        }}
                        disabled={values.evalLead?.length === 4 || !isEditable}
                      >
                        <AddCircleIcon />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {values?.evalLead?.map((num, index) => (
                        <Chip
                          key={`${index?.toString()}-${num}`}
                          label={num}
                          onDelete={() => {
                            const updatedList = values.evalLead.filter((_, i) => i !== index);
                            setFieldValue('evalLead', updatedList);
                          }}
                          color="default"
                          sx={{ mb: 1, mr: 1 }}
                          disabled={!isEditable}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </FormControl>
              </Grid>

              {/* Dynamic Fields Based on Selected Alert Config */}
              {Object.entries(currentConfigProps).map(([key, config]) => {
                if (/_$/.test(key)) return null;

                const { type, enum: enumOptions } = config;

                const label = formatLabel(key); // Main field label

                //  const defaultValue = config.default ?? null;

                const formattedEnumOptions = enumOptions
                  ? enumOptions.map((option) => ({
                      value: option,
                      displayLabel:
                        typeof option === 'string' ? formatLabel(option) : option.toString(),
                    }))
                  : null;

                const fieldValue = values.alertParams[key] ?? config.default;

                // setFieldValue(`alertParams.${key}`, defaultValue)

                if (type === 'boolean') {
                  return (
                    <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                      <FormControl sx={FormStyles} fullWidth>
                        <CustomToggleSwitch
                          label={label}
                          title=""
                          checked={!!fieldValue}
                          onChange={(e) => setFieldValue(`alertParams.${key}`, e.target.checked)}
                          disabled={!isEditable}
                        />
                      </FormControl>
                    </Grid>
                  );
                }

                if (type === 'string' && key === 'pms_sync') {
                  return (
                    <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                      <FormControl sx={FormStyles} fullWidth>
                        <CustomToggleSwitch
                          label={label}
                          title=""
                          checked={
                            typeof fieldValue === 'string'
                              ? Boolean(fieldValue?.toLowerCase())
                              : fieldValue
                          }
                          onChange={(e) => setFieldValue(`alertParams.${key}`, e.target.checked)}
                          disabled={!isEditable}
                        />
                      </FormControl>
                    </Grid>
                  );
                }

                // if (
                //   formattedEnumOptions &&
                //   formattedEnumOptions.length > 0 &&
                //   formattedEnumOptions.length <= 3
                // ) {
                //   return (
                //     <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                //       <FormControl sx={FormStyles} fullWidth>
                //         <Typography variant="subtitle2" gutterBottom>
                //           {label}
                //         </Typography>
                //         <RadioGroup
                //           row
                //           name={`alertParams.${key}`}
                //           value={fieldValue}
                //           onChange={(e) => setFieldValue(`alertParams.${key}`, e.target.value)}
                //         >
                //           {formattedEnumOptions.map(({ value, displayLabel }) => (
                //             <FormControlLabel
                //               key={value.toString()}
                //               value={value.toString()}
                //               control={<Radio size="small" />}
                //               label={displayLabel}
                //             />
                //           ))}
                //         </RadioGroup>
                //       </FormControl>
                //     </Grid>
                //   );
                // }

                if (formattedEnumOptions) {
                  return (
                    <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                      <FormControl sx={FormStyles} fullWidth>
                        <InputLabel>{label}</InputLabel>
                        <Select
                          name={`alertParams.${key}`}
                          value={fieldValue}
                          label={label}
                          onChange={(e) => setFieldValue(`alertParams.${key}`, e.target.value)}
                          disabled={!isEditable}
                        >
                          {formattedEnumOptions.map(({ value, displayLabel }) => (
                            <MenuItem key={value.toString()} value={value.toString()}>
                              {displayLabel}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }

                if (type === 'integer' || type === 'number') {
                  return (
                    <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                      <FormControl sx={FormStyles} fullWidth>
                        <TextField
                          // fullWidth
                          type="number"
                          name={`alertParams.${key}`}
                          label={label}
                          size="small"
                          value={fieldValue}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          disabled={!isEditable}
                        />
                      </FormControl>
                    </Grid>
                  );
                }

                if (type === 'string' && (key === 'targetDateStart' || config.format === 'date')) {
                  return (
                    <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                      <FormControl sx={FormStyles} fullWidth>
                        <TextField
                          disabled={!isEditable}
                          type="date"
                          name={`alertParams.${key}`}
                          label={label}
                          size="small"
                          value={fieldValue}
                          onChange={(e) => {
                            setFieldValue(`alertParams.${key}`, e.target.value);
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ placeholder: '' }}
                        />
                      </FormControl>
                    </Grid>
                  );
                }

                return (
                  <Grid justifyContent="space-around" alignItems="center" px={4} my={4} key={key}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        disabled={!isEditable}
                        name={`alertParams.${key}`}
                        label={label}
                        size="small"
                        value={fieldValue}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </FormControl>
                  </Grid>
                );
              })}
              <Modal
                open={isDelete}
                handleClose={() => setDelete(false)}
                title="Delete Alert"
                content="Are you sure you want to delete the Alert?"
                handleSuccess={() =>
                  deleteUser({
                    organizationId,
                    alertId: alert?.id,
                  })
                }
                closeLabel="Cancel"
                successLabel="Delete"
                variant="contained"
                color="error"
              />
              <Modal
                open={isUpdate}
                handleClose={() => {
                  if (dirty) {
                    handleReset();
                  }
                  setUpdate(false);
                }}
                title="Update Alert"
                content="Are you sure you want to update the Alert?"
                handleSuccess={() => {
                  formElement.current?.click();
                  setUpdate(false);
                }}
                closeLabel="Cancel"
                successLabel="Update"
                variant="contained"
                color="primary"
              />
              <input ref={formElement} style={{ visibility: 'hidden' }} type="submit" />
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};
//
export default AlertDrawerView;
